import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { deleteCommunicationNote, getAllDiagnosis, getCommunicationNote, getDisplayName, updateCommunicationNote, updateCommunicationNoteForStudent } from '@uplift/api';
import { providerPatientRoute } from '@uplift/constants';
import { useCurrentuser } from '@uplift/contexts';
import { DeleteModal, Loader, ProviderLayout, UpliftThemeProvider, extractServerFormValues, useAlert } from '@uplift/ui-v2';
import { Formik, FormikErrors, FormikHelpers } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { commNoteValidation } from './comm-note.validation';
import { isAxiosError } from 'axios';
import { CommNoteForm } from './comm-note.form';
import { useUserQuery } from '@uplift/queries';
import { SubSystem } from '@uplift/types';

export type CommunicationNotePageProps = {
  noteId: string;
  patientId: string;
};

export type CommNoteFormType = {
  submit?: 'later' | 'now';
  diagnosis?: string[];
  cptCodes: string | null;
  comm: Record<string, unknown>;
  risk: Record<string, unknown>;
};

export const CommunicationNotePage: React.VFC<CommunicationNotePageProps> = ({ noteId, patientId }) => {
  const currentUser = useCurrentuser();
  const history = useHistory();
  const queryClient = useQueryClient();
  const snackbar = useAlert();
  const [ deleteOpen, setDeleteOpen ] = useState(false);

  const queryResults = useQueries({
    queries: [
      {
        queryKey: [ 'all-diagnosis' ],
        queryFn: getAllDiagnosis
      },
      {
        queryKey: [ 'medical-notes', noteId ],
        queryFn: () => getCommunicationNote(noteId)
      },
      {
        queryKey: [ 'display-name', patientId ],
        queryFn: () => getDisplayName(patientId)
      }
    ]
  });
  const { data: patient, isLoading: isPatientLoading } = useUserQuery(patientId);
  const [ diagnosisData, noteData, displayNameQuery ] = queryResults;

  const deleteNoteMutation = useMutation({
    mutationFn: () => deleteCommunicationNote(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ 'medical-notes', noteId ] });

      history.push(providerPatientRoute.createLink({ patientId }));
      snackbar.success('Communication note deleted');
    }
  });

  const handleDelete = useCallback(() => {
    setDeleteOpen(true);
  }, []);

  const handleModalClose = useCallback(async (proceed: boolean) => {
    if (proceed) {
      await deleteNoteMutation.mutateAsync();
    }

    setDeleteOpen(false);
  }, [ deleteNoteMutation ]);

  const handleSubmit = useCallback(async (values: CommNoteFormType, helpers: FormikHelpers<CommNoteFormType>) => {
    const updateCommunicationNoteFn = patient.subsystem === SubSystem.higherEd ? updateCommunicationNoteForStudent : updateCommunicationNote;
    const shouldSubmit = values.submit;

    const cptCodes = values.cptCodes ? [ values.cptCodes ] : undefined;
    const response = await updateCommunicationNoteFn(noteId, {
      communicationNoteForm: values.comm,
      riskAssessmentForm: values.risk,
      diagnosisIds: values.comm.billable ? values.diagnosis : [],
      cptCodeIds: values.comm.billable ? cptCodes : [],
    });

    if ('error' in response) {
      snackbar.error('Unable to save note');
      return;
    }

    if (shouldSubmit === 'now') {
      if (response.data.form.errors?.length || response.data.riskAssessmentForm.errors?.length) {
        queryClient.invalidateQueries({ queryKey: [ 'medical-notes', noteId ], refetchType: 'none' });

        const errors: FormikErrors<CommNoteFormType> = {
          comm: response.data.form.errors.reduce((acc, cur) => ({
            ...acc,
            [cur.id]: cur.message
          }), {}),
          risk: response.data.riskAssessmentForm.errors.reduce((acc, cur) => ({
            ...acc,
            [cur.id]: cur.message
          }), {})
        };

        helpers.setErrors(errors);

        return;
      }

      try {
        const submitResponse = await updateCommunicationNoteFn(noteId, {
          communicationNoteForm: values.comm,
          riskAssessmentForm: values.risk,
          diagnosisIds: values.comm.billable ? values.diagnosis : [],
          cptCodeIds: values.comm.billable ? cptCodes : [],
          isSubmitted: true
        });

        if ('error' in submitResponse) {
          snackbar.error('Unable to submit note');
          return;
        }
      } catch (err) {
        if (isAxiosError(err) && err.response?.data?.error) {
          snackbar.error(err.response.data.error);
          return;
        }

        snackbar.error('Error saving note');

        return;
      }
    }

    history.push(providerPatientRoute.createLink({ patientId }));
  }, [ history, noteId, patientId, queryClient, snackbar, patient?.subsystem ]);

  const isLoading = useMemo(() => queryResults.some(x => x.isLoading) || isPatientLoading, [ queryResults, isPatientLoading ]);

  const initialValues: CommNoteFormType = useMemo(() => {
    if (isLoading) {
      return {
        comm: {},
        risk: {},
        diagnosis: [],
        cptCodes: null
      };
    }

    const note = noteData.data?.data;

    return {
      comm: {
        ...extractServerFormValues(note?.form.fields),
      },
      risk: {
        ...extractServerFormValues(note?.riskAssessmentForm.fields),
      },
      diagnosis: note.diagnosisIds,
      cptCodes: note.cptCodeIds?.length > 0 ? note.cptCodeIds[0] : null,
    };
  }, [ isLoading, noteData.data?.data ]);

  const content = useMemo(() => {
    if (isLoading) {
      return <Loader />;
    }

    const note = noteData.data.data;

    return (
      <Formik
        onSubmit={handleSubmit}
        enableReinitialize
        initialValues={initialValues}
        validationSchema={commNoteValidation}
      >
        <CommNoteForm
          commNoteForm={note.form}
          diagnoses={diagnosisData.data.data}
          displayName={displayNameQuery.data.data.full}
          medicalRole={currentUser.medicalRole}
          riskAssessmentForm={note.riskAssessmentForm}
          userId={currentUser.id}
          handleDelete={handleDelete}
        />
      </Formik>
    );
  }, [ currentUser.id, currentUser.medicalRole, diagnosisData, displayNameQuery.data?.data?.full, handleDelete, handleSubmit, initialValues, isLoading, noteData.data?.data ]);

  return (
    <ProviderLayout
      logoLink=""
      background="white"
      navigationVariant="clear"
      showClientsSearch={false}
      showNavigationLinks={false}
    >
      <UpliftThemeProvider>
        {content}

        <DeleteModal
          open={deleteOpen}
          title="Delete this note?"
          onClose={handleModalClose}
          loading={deleteNoteMutation.isLoading}
        />
      </UpliftThemeProvider>
    </ProviderLayout>
  );
};
